// Single source of truth for the marketplace service categories.
//
// UK-wide pivot, Feb 2026: expanded from 4 generic buckets (hair / nails /
// lashes / makeup) to 7 pillars that reflect the actual specialisms in the
// Afro-textured, multi-cultural beauty community. The old `hair` bucket has been split
// into Locs/Braids/Protective, Hair Care & Styling, and Barbering &
// Grooming because those are three very different stylist worlds.
//
// Icon names are exported as lucide-react component references so each
// consumer can render them directly. Color classes are Tailwind utility
// pairs used on the rounded icon bubble (background + foreground).
import { Crown, Sparkles, Scissors, Sun, Gem, Eye, Palette } from 'lucide-react';

export const CATEGORIES = [
  {
    id: 'locs_braids_protective',
    name: 'Locs, Braids & Protective',
    icon: Crown,
    color: 'bg-amber-50 text-amber-600',
  },
  {
    id: 'hair_styling_care',
    name: 'Hair Care & Styling',
    icon: Sparkles,
    color: 'bg-rose-50 text-rose-600',
  },
  {
    id: 'barbering_grooming',
    name: 'Barbering & Grooming',
    icon: Scissors,
    color: 'bg-slate-100 text-slate-700',
  },
  {
    id: 'aesthetics_skin',
    name: 'Aesthetics & Skin',
    icon: Sun,
    color: 'bg-yellow-50 text-yellow-700',
  },
  {
    id: 'nails_nail_art',
    name: 'Nails & Nail Art',
    icon: Gem,
    color: 'bg-fuchsia-50 text-fuchsia-600',
  },
  {
    id: 'lashes_brows',
    name: 'Lashes & Brows',
    icon: Eye,
    color: 'bg-purple-50 text-purple-600',
  },
  {
    id: 'makeup_glam',
    name: 'Make-up & Glam',
    icon: Palette,
    color: 'bg-pink-50 text-pink-600',
  },
];

// Old → new mapping for back-compat. Any link to `?category=hair` (e.g.
// from SEO crawls, Instagram bios, the founders' WhatsApp threads) lands
// on the closest new pillar instead of an empty page. `hair` is the
// trickiest, the legacy bucket covered braids + barbering + styling, so
// we default to the most general "Hair Care & Styling" and let users
// re-filter from there.
export const LEGACY_CATEGORY_MAP = {
  hair: 'hair_styling_care',
  nails: 'nails_nail_art',
  lashes: 'lashes_brows',
  makeup: 'makeup_glam',
};

export const CATEGORY_IDS = CATEGORIES.map((c) => c.id);

// Helper: returns the new category id if the input is a legacy id, the
// input itself if it's already a valid new id, or `null` if it's neither
// (caller can then decide whether to show an "unknown category" state).
export function resolveCategoryId(input) {
  if (!input) return null;
  if (CATEGORY_IDS.includes(input)) return input;
  if (LEGACY_CATEGORY_MAP[input]) return LEGACY_CATEGORY_MAP[input];
  return null;
}

// Lookup helper for components that just need the display name.
export function getCategoryName(id) {
  const cat = CATEGORIES.find((c) => c.id === id);
  return cat ? cat.name : id;
}
